import {useForm} from 'antd/lib/form/Form'
import {useCallback, useState} from 'react'
import validator from '../classes/validator'
import extractor from '../classes/extractor'
import GlobalOptionsInterface from '../types/global-options'
import Api from '../classes/api'
import {
    createErrorState,
    createInitialState,
    createStartedState,
    createSuccessState,
} from '../../../../common/helpers/create-request-state'
import CostInfoInterface from '../types/models/cost-info'
import CostStateType from '../types/state/cost-state'
import useSteps from '../../../common/hooks/use-steps'
import {log} from '../../../../common/helpers/console/log'
import IDS from '../constants/ids'
import Modal from 'antd/lib/modal'
import 'antd/lib/modal/style'

interface UseControllerArgsInterface {
    globalOptions: GlobalOptionsInterface
}

const costInitState = createInitialState<CostInfoInterface>()

const useController = ({globalOptions}: UseControllerArgsInterface) => {
    const [form] = useForm(),
        {currentStep, prevStep, nextStep} = useSteps(),
        [cost, setCost] = useState<CostStateType>(costInitState),
        /*
            Пересчет стоимости,
            поля должны быть валидны!
         */
        recalculate = useCallback(async () => {
            // Сразу устанавливаем флаг, о начале запроса стоимости
            setCost(createStartedState())
            try {
                // Извлекаем данные для получения стоимости
                const data = extractor.extractDataForGetCost(),
                    result = await Api.getCost(data)

                // Проверяем результат запроса
                if ('errors' in result) throw new Error(result.errors.join())
                else
                    form.setFieldsValue({
                        [IDS.CALCULATION_ID]: (result as CostInfoInterface).calculationId,
                    }) // Сохраняем идентификатор расчёта в поле
                setCost(createSuccessState(result))
                return true
            } catch (e) {
                setCost(createErrorState([e.message]))
                console.error(e)
                return false
            }
        }, []),
        requestCallback = useCallback(async (): Promise<any> => {
            log('requestCallback')
            const dataForCallback = extractor.extractDataForRequest()
            log('Извлечены данные для отправки заявки', dataForCallback)
            const calculationId = form.getFieldValue(IDS.CALCULATION_ID)

            return await Api.requestCallback(dataForCallback, calculationId)
        }, []),
        /*
            Обработчик перехода с первого на второй шаг
            Параметры груза -> Параметры перевозки
         */
        fromFirstToSecondStep = useCallback(async e => {
            console.log('fromFirstToSecondStep')
            e.preventDefault()
            const validationStatus = await validator.validateFirstStep()
            if (validationStatus === 'error') return
            // Сразу устанавливаем флаг, о начале запроса стоимости
            setCost(createStartedState())
            const isSuccess = await recalculate()
            if (isSuccess) nextStep()
        }, []),
        /*
            Обработчик перехода со второго на третий шаг
            Параметры перевозки -> Расчет и договор
         */
        fromSecondToThirdStep = useCallback(async e => {
            console.log('fromSecondToThirdStep')
            e.preventDefault()
            const validationStatus = await validator.validateSecondStep()
            if (validationStatus === 'error') return
            const isSuccess = await recalculate()
            if (isSuccess) nextStep()
        }, []),
        /*
            Обработчик перехода с третьего на финальный шаг
            Расчет и договор -> Модалка об отправке заявки
         */
        fromThirdToFourthStep = useCallback(async e => {
            console.log('fromThirdToFourthStep')
            e.preventDefault()
            const validationStatus = await validator.validateThirdStep()
            if (validationStatus === 'error') return
            // (только ради того, чтобы отправвить email в данные расчёта, так как email запихан в метод расчёта цены)
            const recalculationIsSuccess = await recalculate()
            if (!recalculationIsSuccess) {
                Modal.error({
                    title: 'Ошибка расчёта параметров',
                    content: 'При проведении расчёта произошла ошибка.',
                })
                return
            }
            //
            const isSuccess = await requestCallback()
            if (isSuccess) {
                nextStep()
            } else {
                Modal.error({
                    title: 'Ошибка регистрации запроса',
                    content:
                        'При сохранении расчёта произошла ошибка. Пожалуйста, свяжитесь со службой поддержки ',
                })
            }
        }, []),
        /*
            Финальный шаг с результатом
         */
        lastStep = useCallback(async e => {
            console.log('lastStep')
            e.preventDefault()
        }, []),
        handleNextClick = useCallback(
            e => {
                if (currentStep === 0) return fromFirstToSecondStep(e)
                if (currentStep === 1) return fromSecondToThirdStep(e)
                if (currentStep === 2) return fromThirdToFourthStep(e)
                if (currentStep === 3)
                    return (e => {
                        location.reload()
                    })() // перезагружаем страницу для нового расчёта
            },
            [currentStep]
        )

    validator.setForm(form)
    extractor.setForm(form)
    extractor.setGlobalOptions(globalOptions)

    return {
        form,
        currentStep,
        cost,
        prevStep,
        recalculate,
        handleNextClick,
    }
}

export default useController
